// components/DashboardChart.tsx
import React from "react";
import { ResponsiveContainer } from "recharts";

import AreaChart from "./charts/AreaChart";
import AreaLineChart from "./charts/AreaLineChart";
import BarChart from "./charts/BarChart";
import LineChart from "./charts/LineChart";
import PieChart from "./charts/PieChart";

import { ChartConfig } from "../data/chartConfiguartion";
import { ChartType } from "../data/chartConfiguartion";

import "../styles/DashboardChart.scss";
import AssetCategoryChart from "./charts/assets/AssetCategoryChart";
import AssetCategoryPieChart from "./charts/assets/AssetCategoryPieChart";
import AntdDataTable from "./charts/DataTable";
import LeadSourcesChart from "./charts/leads/LeadSourcesChart";
import DailyLeadPerformanceChart from "./charts/leads/DailyLeadPerformanceChart";
import LeadSourcesPieChart from "./charts/leads/LeadSourcesPieChart";
import LeadSourcesDataTable from "./charts/leads/LeadSourcesDataTable";
import PipelineStageDistributionChart from "./charts/leads/PipelineStageDistributionChart";
import FormKeyStatsChart from "./charts/leads/FormKeyStatsChart";
import PerformersStageWiseDataTable from "./charts/leads/PerformersStageWiseDataTable";
import SourcesStageWiseDataTable from "./charts/leads/SourcesStageWiseDataTable";

interface DashboardChartProps {
  title: string;
  description?: string;
  chartType: ChartType;
  data: any[];
  config: ChartConfig["config"];
  loading?: boolean;
  error?: string;
}

const DashboardChart: React.FC<DashboardChartProps> = ({
  title,
  description,
  chartType,
  data,
  config,
  loading = false,
  error,
}) => {
  const commonProps = {
    title,
    description,
    data,
    loading,
    error,
    height: 350,
  };

  // Render different chart types
  const renderChart = () => {
    if ((!data || data.length === 0) && chartType != "form_key_stats_chart") {
      return (
        <div className="chart-empty-state">
          <i className="ti ti-chart-bar text-muted" />
          <p className="text-muted">No data available</p>
        </div>
      );
    }
    switch (chartType) {
      case "assets_category_chart":
        return <AssetCategoryChart data={data} itemsPerPage={5} />;

      case "assets_category_pie_chart":
        return <AssetCategoryPieChart data={data} />;

      case "lead_sources_chart":
      case "deal_sources_chart":
        return <LeadSourcesChart data={data} itemsPerPage={5} />;

      case "daily_lead_trends":
      case "daily_deal_trends":
        return <DailyLeadPerformanceChart data={data} />;

      case "lead_sources_pie_chart":
      case "deal_sources_pie_chart":
        return <LeadSourcesPieChart data={data} />;

      case "lead_sources_table":
      case "deal_sources_table":
        return <LeadSourcesDataTable data={data} />;

      case "lead_performers_stage_wise_table":
      case "deal_performers_stage_wise_table":
        return <PerformersStageWiseDataTable data={data} />;

      case "lead_sources_stage_wise_table":
      case "deal_sources_stage_wise_table":
        return <SourcesStageWiseDataTable data={data} />;

      case "pipeline_stage_distribution":
      case "deal_pipeline_stage_distribution":
        return <PipelineStageDistributionChart data={data} />;

      case "table":
        return (
          <AntdDataTable
            data={data}
            columnKey={config?.columns}
            loading={loading}
            error={error}
            height={800}
            showPagination={config?.pagination}
            pageSize={4}
          />
        );

      case "area-line":
        const chartLines =
          config.series?.map((series) => ({
            dataKey: series.dataKey,
            name: series.name,
            stroke: series.stroke,
            fill: series.fill,
            strokeWidth: series.strokeWidth || 2,
            type: series.area ? ("area" as const) : ("line" as const),
            dot: series.dot || false,
          })) || [];

        return (
          <AreaLineChart
            {...commonProps}
            xAxisKey={config.xAxis?.field || "date"}
            lines={chartLines}
            showGrid={config.showGrid}
          />
        );

      case "bar":
        return (
          <BarChart
            {...commonProps}
            xAxisKey={config.xAxis?.field || "name"}
            yAxisKey={config.yAxis?.field || "value"}
            barColor={config.colors?.[0] || "#3b82f6"}
            showGrid={config.showGrid}
          />
        );

      case "line":
        return (
          <LineChart
            {...commonProps}
            xAxisKey={config.xAxis?.field || "name"}
            yAxisKey={config.yAxis?.field || "value"}
            lineColor={config.colors?.[0] || "#3b82f6"}
            showGrid={config.showGrid}
          />
        );

      case "area":
        return (
          <AreaChart
            {...commonProps}
            xAxisKey={config.xAxis?.field || "name"}
            yAxisKey={config.yAxis?.field || "value"}
            areaColor={config.colors?.[0] || "#3b82f6"}
            showGrid={config.showGrid}
          />
        );

      case "pie":
      case "donut":
        return (
          <PieChart
            {...commonProps}
            valueKey={config.valueField || "value"}
            nameKey={config.labelField || "name"}
            colors={config.colors}
            showLegend={config.showLegend}
            isDonut={chartType === "donut"}
          />
        );

      case "form_key_stats_chart":
        return <FormKeyStatsChart type={config?.type || 1} />;

      default:
        return (
          <div className="chart-error">
            <p>Unsupported chart type: {chartType}</p>
          </div>
        );
    }
  };

  if (error) {
    return (
      <div className="dashboard-chart-container error">
        <div className="chart-header">
          <div className="chart-title-section">
            <h6 className="chart-title">{title}</h6>
            {description && <p className="chart-description">{description}</p>}
          </div>
        </div>
        <div className="chart-error-state">
          <i className="ti ti-alert-circle text-danger" />
          <p className="text-danger">Error loading data!</p>
          <small className="text-muted">{error}</small>
        </div>
      </div>
    );
  }

  return (
    <div className="dashboard-chart-container">
      <div className="chart-header">
        <div className="chart-title-section">
          <h6 className="chart-title">{title}</h6>
        </div>
        <div className="chart-actions">
          <i className="info-icon ti ti-info-circle ms-2 text-info" />
          <div className="tooltip-text">{description}</div>
        </div>
      </div>

      <div className="chart-wrapper">
        {loading ? (
          <div className="chart-loading-state">
            <div className="loading-spinner">
              <i className="ti ti-loader-2 spin" />
            </div>
            <p>Loading chart data...</p>
          </div>
        ) : (
          <ResponsiveContainer width="100%" height="100%">
            {renderChart()}
          </ResponsiveContainer>
        )}
      </div>
    </div>
  );
};

export default DashboardChart;
